import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Table, Tag, Empty, Tooltip, Button, Space, Card, Input, Select } from 'antd';
import { TeamOutlined, RobotOutlined, UserOutlined, LineChartOutlined, SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { getMoodlePrograms } from '../services/moodleService';
import { useAuth } from '../hooks/useAuth';
import { Training, TrainingType } from '../types/training';

const { Title, Text } = Typography;

const StakeholderDashboard: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [programs, setPrograms] = useState<Training[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [filterMode, setFilterMode] = useState('');

    useEffect(() => {
        if (!user?.username) return;
        setLoading(true);
        getMoodlePrograms(user.username)
            .then((data: Training[]) => setPrograms(data.filter((p: Training) => p.isStakeholder)))
            .catch((error) => console.error('Error loading stakeholder programs', error))
            .finally(() => setLoading(false));
    }, [user]);

    const filteredPrograms = useMemo(() => {
        return programs.filter((p) => {
            if (searchText) {
                const q = searchText.toLowerCase();
                if (!p.trainingName?.toLowerCase().includes(q) && !p.trainingId?.toLowerCase().includes(q)) {
                    return false;
                }
            }
            if (filterMode === 'TrainerLed' && p.programType !== 'TrainerLed') return false;
            if (filterMode === 'AI' && p.programType === 'TrainerLed') return false;
            return true;
        });
    }, [programs, searchText, filterMode]);

    const isFiltered = !!(searchText || filterMode);

    const columns = [
        {
            title: 'Training ID',
            dataIndex: 'trainingId',
            key: 'trainingId',
            render: (id: string) => (
                <Tooltip title={id}>
                    <div style={{
                        maxWidth: '120px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontFamily: 'monospace',
                        fontSize: '12px'
                    }}>
                        {id}
                    </div>
                </Tooltip>
            )
        },
        {
            title: 'Name',
            dataIndex: 'trainingName',
            key: 'trainingName',
            render: (name: string, record: Training) => (
                <Typography.Link onClick={() => navigate(`/stakeholder/${encodeURIComponent(record.trainingId)}`)}>
                    {name}
                </Typography.Link>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'trainingType',
            key: 'trainingType',
            render: (type: TrainingType) => {
                let color = 'blue';
                if (type === TrainingType.OPEN) color = 'green';
                if (type === TrainingType.REQUESTABLE) color = 'orange';
                return <Tag color={color}>{type || TrainingType.PRIVATE}</Tag>;
            }
        },
        {
            title: 'Mode',
            key: 'programType',
            render: (_: any, record: Training) => {
                const isTrainerLed = record.programType === 'TrainerLed';
                return (
                    <Tag
                        icon={isTrainerLed ? <UserOutlined /> : <RobotOutlined />}
                        color={isTrainerLed ? 'purple' : 'cyan'}
                        style={{ borderRadius: 4, fontWeight: 500 }}
                    >
                        {isTrainerLed ? 'Trainer Led' : 'AI Led'}
                    </Tag>
                );
            },
        },
        {
            title: 'Status',
            key: 'active',
            render: (_: any, record: Training) => (
                record.active ? <Tag color="success">ACTIVE</Tag> : <Tag color="error">INACTIVE</Tag>
            ),
        },
        {
            title: 'Created By',
            key: 'createdBy',
            width: 200,
            render: (_: any, record: Training) => (
                record.createdBy
                    ? <Tooltip title={record.createdBy}><span style={{ fontSize: '12px', color: '#595959' }}>{record.createdBy}</span></Tooltip>
                    : <span style={{ color: '#bfbfbf', fontSize: '12px' }}>—</span>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: Training) => (
                <Space size="middle">
                    <Button
                        icon={<LineChartOutlined />}
                        onClick={() => navigate(`/stakeholder/${encodeURIComponent(record.trainingId)}`)}
                        type="primary"
                        ghost
                        style={{ width: '110px', borderColor: '#fa541c', color: '#fa541c', background: 'transparent' }}
                    >
                        Analyze
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px', maxWidth: '95%', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '32px' }}>
                <div style={{
                    width: 56, height: 56, borderRadius: '12px',
                    backgroundColor: '#fff7e6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginRight: 20
                }}>
                    <TeamOutlined style={{ fontSize: '28px', color: '#fa541c' }} />
                </div>
                <div>
                    <Title level={2} style={{ margin: 0 }}>Stakeholder Dashboard</Title>
                    <Text type="secondary">Programs where you're listed as a stakeholder - read-only progress summaries</Text>
                </div>
            </div>

            <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                    <Input
                        placeholder="Search by name or ID..."
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        allowClear
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        style={{ width: 280 }}
                    />
                    <Select
                        value={filterMode || undefined}
                        placeholder="All Modes"
                        allowClear
                        onClear={() => setFilterMode('')}
                        onChange={val => setFilterMode(val || '')}
                        style={{ width: 150 }}
                        options={[
                            { value: 'TrainerLed', label: 'Trainer Led' },
                            { value: 'AI', label: 'AI Led' },
                        ]}
                    />
                    {isFiltered && (
                        <Button
                            size="small"
                            icon={<FilterOutlined />}
                            onClick={() => { setSearchText(''); setFilterMode(''); }}
                        >
                            Clear filters
                        </Button>
                    )}
                    <div style={{ marginLeft: 'auto' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {isFiltered
                                ? `${filteredPrograms.length} of ${programs.length} programs`
                                : `${programs.length} programs`}
                        </Text>
                    </div>
                </div>

                <Table
                    columns={columns}
                    dataSource={filteredPrograms}
                    loading={loading}
                    rowKey="trainingId"
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: (
                            <Empty description="You are not currently listed as a stakeholder on any program." />
                        ),
                    }}
                />
            </Card>
        </div>
    );
};

export default StakeholderDashboard;
